import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Text, Button } from '@nula/core-ui';

import { useWorkspaceStore } from '@/stores/use-workspace-store';

import styles from '../app.module.css';

/**
 * Workspace-recovery screen.
 *
 * Renders when useBootState() returns 'onboarding-required' — an authenticated
 * user whose /api/workspaces fetch returned []. This state should be
 * unreachable: every signup auto-provisions a PERSONAL workspace server-side
 * (create_personal_workspace_tx when is_new_user). If we land here, the client
 * simply re-fetches from the server and lets the boot state machine derive to
 * 'ready' once the personal workspace surfaces.
 *
 * It deliberately does NOT create a workspace: a client-side create would only
 * ever produce a TEAM (personal is server-owned), which would strand the user
 * in a lapsed, read-only, unpaid team. Recovery is a re-fetch, not a create.
 */
export function OnboardingFlow() {
  const [pending, setPending] = useState(false);

  async function refresh() {
    setPending(true);
    try {
      await useWorkspaceStore.getState().refreshFromServer();
      // BootState derives to 'ready' once refreshFromServer sets
      // activeWorkspaceId; BootGate re-renders the shell automatically.
    } catch (err) {
      console.error('[OnboardingFlow] workspace refresh failed', err);
    } finally {
      setPending(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <motion.div
      key="onboarding-flow"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={styles.splash}
    >
      <motion.div
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        className={styles.splashOrb}
        style={{ '--orb-size': '500px' } as React.CSSProperties}
      />
      <motion.div
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        className={styles.splashOrb2}
        style={{ '--orb-size': '600px' } as React.CSSProperties}
      />

      <div className={styles.onboardingCard}>
        <Text className={styles.onboardingHeadline}>Welcome to Nula</Text>
        <Text type="secondary" className={styles.onboardingBody}>
          Setting up your workspace…
        </Text>
        <Button
          variant="primary"
          size="lg"
          onClick={() => void refresh()}
          loading={pending}
          disabled={pending}
          className={styles.onboardingCta}
        >
          Retry
        </Button>
      </div>
    </motion.div>
  );
}
